import { useEffect, useRef, useState, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';

/**
 * useDateStats — SWR-like 60 s TTL cache for per-day planning stats used by
 * the V4 DataAwareDatePicker (PRD §4.13 + 11.4 required hook).
 *
 * V4 rule: the stats query MUST cover the full 7 × N visible calendar grid
 * (Mon-anchored), NOT just the month boundaries. Every rendered day cell —
 * including adjacent-month cells — is a normal data-aware cell and needs
 * its own stats payload.
 *
 * Returns:
 *   {
 *     stats: { [ISO_DATE]: DateStats },
 *     state: 'loading' | 'loaded' | 'stale' | 'error',
 *     refresh: () => void,
 *     range: { start_date, end_date },  // the range that was fetched
 *   }
 *
 * Scope lets multi-user/org views share the same cache partition.
 */
const CACHE = new Map(); // key = `${start}|${end}|${scope}` → { fetchedAt, data, inFlight }
const TTL_MS = 60_000;

function scopeKey(scope = {}) {
  const a = scope.actingAs || scope.acting_as_user_id || '';
  const o = scope.orgId || scope.org_id || '';
  return `${a}#${o}`;
}

/** Compute the full 7 × N visible grid Mon-anchored for a given month. */
export function computeVisibleGridRange(month) {
  const m = month || new Date();
  const first = startOfMonth(m);
  const last = endOfMonth(m);
  const start = startOfWeek(first, { weekStartsOn: 1 });
  const end = endOfWeek(last, { weekStartsOn: 1 });
  return {
    start_date: format(start, 'yyyy-MM-dd'),
    end_date: format(end, 'yyyy-MM-dd'),
  };
}

export function useDateStats(month, scope) {
  const [state, setState] = useState('loading');
  const [stats, setStats] = useState({});
  const abortRef = useRef(null);

  const { start_date, end_date } = computeVisibleGridRange(month);
  const key = `${start_date}|${end_date}|${scopeKey(scope)}`;

  const fetchStats = useCallback(
    async ({ force = false } = {}) => {
      const cached = CACHE.get(key);
      const isFresh = cached && Date.now() - cached.fetchedAt < TTL_MS;

      if (cached?.data) {
        setStats(cached.data);
        setState(isFresh ? 'loaded' : 'stale');
      } else {
        setStats({});
        setState('loading');
      }

      if (!force && isFresh) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const params = new URLSearchParams({ start_date, end_date });
        if (scope?.actingAs) params.set('acting_as', scope.actingAs);
        if (scope?.orgId) params.set('org_id', scope.orgId);
        const res = await fetch(`/api/tasks/date-stats?${params}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        const next = {};
        for (const r of rows) next[r.date] = r;
        CACHE.set(key, { fetchedAt: Date.now(), data: next });
        setStats(next);
        setState('loaded');
      } catch (err) {
        if (err.name === 'AbortError') return;
        setState((s) => (s === 'loaded' || s === 'stale' ? s : 'error'));
      }
    },
    [key, start_date, end_date, scope?.actingAs, scope?.orgId],
  );

  useEffect(() => {
    fetchStats();
    return () => abortRef.current?.abort();
  }, [fetchStats]);

  return {
    stats,
    state,
    refresh: () => fetchStats({ force: true }),
    range: { start_date, end_date },
  };
}

export default useDateStats;
